// ---------------------------------------------------------------------------
// Archived tasks — the way back. Archive was a one-way door from this app
// (recovery meant Odoo backend); now any member can browse + restore.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { Archive, RotateCcw, X } from 'lucide-react';
import type { TaskListItem } from '@cg-projects/shared';
import { api } from '@/core/api/apiClient';
import { Icon } from '@/core/ui/icons';
import { useTasksStore } from '@/features/tasks/store/tasksStore';
import { useToastStore } from '@/core/ui/toastStore';
import { useEscapeToClose } from '@/core/ui/useEscapeToClose';
import { Spinner, StateBadge } from '@/core/ui/atoms';

export function ArchivedTasksModal({ areaId, onClose }: { areaId: number; onClose: () => void }) {
  useEscapeToClose(onClose);
  const toast = useToastStore((s) => s.show);
  const [rows, setRows] = useState<TaskListItem[] | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    api.get<{ tasks: TaskListItem[] }>(`/areas/${areaId}/tasks/archived`)
      .then((r) => { if (alive) setRows(r.tasks); })
      .catch((e) => { if (alive) { setRows([]); toast(e instanceof Error ? e.message : 'Could not load archived tasks.', 'error'); } });
    return () => { alive = false; };
  }, [areaId, toast]);

  const restore = async (t: TaskListItem) => {
    if (busyId) return;
    setBusyId(t.id);
    try {
      await api.post(`/tasks/${t.id}/unarchive`, {});
      setRows((prev) => (prev ?? []).filter((x) => x.id !== t.id));
      // Say where it actually went — sub-tasks live under their parent, and
      // done tasks only show when "Completed" is toggled on.
      const where = t.parentId
        ? 'restored — find it inside its parent task.'
        : (t.state === '1_done' || t.state === '1_canceled')
          ? 'restored — tick "Completed" in the filter bar to see it.'
          : "restored — it's back on the board.";
      toast(`"${t.name}" ${where}`, 'info');
      const st = useTasksStore.getState();
      void st.loadTasks(areaId, st.loadedClosed[areaId] ?? false, true);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not restore.', 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/60 animate-fade-in" onClick={onClose} />
      <div className="fixed inset-x-4 top-16 z-50 mx-auto max-w-lg overflow-hidden rounded-xl border border-line bg-canvas shadow-2xl animate-fade-in">
        <div className="flex h-11 items-center justify-between border-b border-line px-4">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink"><Icon i={Archive} size={14} /> Archived tasks{rows?.length ? ` · ${rows.length}` : ''}</span>
          <button onClick={onClose} className="rounded p-1 text-muted hover:bg-hover hover:text-ink" title="Close (Esc)"><Icon i={X} size={14} /></button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-3">
          {rows === null && <Spinner label="Loading archived tasks…" />}
          {rows !== null && rows.length === 0 && <div className="py-8 text-center text-sm text-faint">Nothing archived in this project.</div>}
          {(rows ?? []).map((t) => (
            <div key={t.id} className="mb-1.5 flex items-center gap-2 rounded-md border border-line bg-page px-3 py-2">
              <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{t.name}</span>
              <StateBadge state={t.state} />
              <button
                onClick={() => void restore(t)}
                disabled={busyId !== null}
                className="flex shrink-0 items-center gap-1 rounded-md border border-line px-2 py-1 text-[11px] text-muted transition hover:bg-hover hover:text-ink disabled:opacity-40"
              >
                {busyId === t.id ? 'Restoring…' : <><Icon i={RotateCcw} size={14} /> Restore</>}
              </button>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
